import type { LocalizePathParams } from "#modules/localization/pathNames/localizePath"
import type { LocalizationUtilsConfig } from "#modules/localization/pathNames/types"
import type { Props } from "#types/utils"
import type { UrlObject } from "node:url"

import NextLink from "next/link"

import { internalLocalizePath } from "#modules/localization/pathNames/localizePath"
import { getSortedPathnames } from "#modules/localization/pathNames/pathNameUtils"

export function createLocalizationUtils<Locales extends readonly string[], Locale extends Locales[number], DefaultLocale extends Locales[number]>(
  config: LocalizationUtilsConfig<Locales, DefaultLocale>,
) {
  // Create once so the mapping is not re-sorted on every call
  const sortedRouteLocalizationMapping = getSortedPathnames(Object.keys(config.routeLocalizationMapping))

  const _localizePath = (params: Omit<LocalizePathParams<Locale>, "sortedRouteLocalizationMapping">) =>
    internalLocalizePath({ ...config, ...params, sortedRouteLocalizationMapping })

  const Link: (props: Props<typeof NextLink> & { locale: Locale; localizedSlugs?: Partial<Record<Locale, string>> }) => JSX.Element = ({
    locale,
    localizedSlugs,
    href,
    ...rest
  }) => {
    let _href
    if (typeof href === "string") {
      _href = _localizePath({ pathName: localizedSlugs ? translateLocalizedSlug(href, locale, config.defaultLocale, localizedSlugs) : href, locale: locale })
    } else {
      // Href is an url object
      _href = {
        ...href,
        pathname: _localizePath({
          pathName: localizedSlugs ? translateLocalizedSlug(href.pathname!, locale, config.defaultLocale, localizedSlugs) : href.pathname!,
          locale: locale,
        }),
      } satisfies UrlObject
    }

    return <NextLink {...rest} href={_href} />
  }

  return {
    localizePath: _localizePath,
    Link,
  }
}

// Pathname with unlocalized slug
function translateLocalizedSlug(pathName: string, locale: string, defaultLocale: string, localizedSlugs: Partial<Record<string, string>>) {
  let localizedSlug = localizedSlugs[locale]
  if (!localizedSlug) {
    if (!localizedSlugs[defaultLocale]) {
      throw new Error("At least default locale must be given inside the localized slugs object")
    }
    localizedSlug = localizedSlugs[defaultLocale]
  }

  const splitPath = pathName.split("/")
  splitPath.pop()

  return splitPath.join("/") + "/" + localizedSlug
}
